/** @jsxImportSource @emotion/react */
import {css} from '@emotion/react';
import background from '../assets/images/backgroundDark.png';
import {LIGHT_GREY, RED_CREEK, WHITE} from '../styles/colors';
import {FADE_IN_ANIMATION} from '../styles/animations';
import {LAYER} from '../layout/zIndex';
import {onIe} from '../language/browser';
import {useDispatch, useSelector} from 'react-redux';
import {IAppState} from '../core/redux/IAppState';
import {useEffect} from 'react';
import {getResortDetailsAction} from '../resortDetails/getResortDetailsAction';
import {getEntitlementTypesAction} from '../entitlementTypes/getEntitlementTypesAction';
import PageHeader from '../pageHeader/PageHeader';
import ResetUserPassword from './ResetUserPassword';
import {Link, Outlet, useLocation} from 'react-router-dom';
import { PRODUCT_ADMINISTRATION, PRODUCT_MAPPING_ROUTE} from '../core/routes';
import * as React from 'react';
import errorImgRed from '../assets/icons/failureTriangleRed.svg';
import { getResortMappingHasErrorAction } from '../resortMappingTool/getResortMappingHasErrorAction';
import { getResource } from '../language/resources/getResource';
import { setLocale } from '../language/resources/setLocaleAction';
import { setDateFormat } from '../language/setDefaultDate';
import { getScriptVersionAction } from '../admin/systemAdmin/getScriptVersionAction';
import { getUserSearchHistoryAction } from '../guestSearch/getUserSearchHistoryAction';

const darkBackgroundStyle = css`
  background: url('${background}') no-repeat center center fixed;
  background-size: cover;
`;

const styles = {
  root: (useDarkBackground?: boolean) => css`
    display: flex;
    flex-direction: column;
    height: 0;
    min-height: 100%;
    background-color: ${LIGHT_GREY};
    ${FADE_IN_ANIMATION};
    ${useDarkBackground && darkBackgroundStyle};
  `,
  headerWrapper:  css`

  `,
  envBanner:   css`
    width: 100%;
    background-color: ${RED_CREEK};
    font-size: 50px;
    text-align: center;
    font-weight: 600;
    letter-spacing: 5px;
    color: ${WHITE};
    display: inline-block;
  `,
  mappingBanner: css `
    background-color: #f6ca30;
    text-align: center;
    padding-top: 3px;
    padding-bottom: 3px;
    display: inline-block;
    width: 100%;
  `,
  mappingWarning: css `
    width: 18px;
    position: relative;
    top: 3px;
    margin-right: 5px;
    margin-left: 5px;
  `,
  mainContentWrapper: css`
    overflow: auto;
  `,
  mainContent: css`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 400px;
    margin: 0 auto; /* center main content horizontally */
    box-sizing: border-box;
    width: 90%;
    min-height: 500px;
    transition: all .5s ease;
    z-index: ${LAYER.ONE.MAIN_CONTENT};

    /* On smaller screens, use smaller margin (here, smaller = 2 cards per row) */
    @media(max-width: 960px) {
      width: 95%;
    }

    ${
            // IE is not good at allocating extra space to dynamic elements in flexbox-justified layouts. If we don't specify a 
            // hard dimension somewhere in the hierarchy, IE incorrectly gives GuestCards more space than they have, and they 
            // extend outside the bounds of their parent. So, we give IE some hard breakpoint with hard widths to make sure 
            // cards are laid out nicely. It's not great, but it's flat and in one place. 
            // Do not use max-width here, that is too dynamic.
            onIe() ? css`
                      /* 1 small card per row */
                      @media(min-width: 0px) { width: 400px; margin: 0 auto; padding: 0 10px; }

                      /* 1 big card per row */
                      @media(min-width: 590px) { width: 460px; margin: 0 auto; }

                      /* 2 Cards per row */
                      @media(min-width: 960px) { width: 860px; }

                      /* 3 Cards per row */
                      @media(min-width: 1320px) { width: 1265px; }

                      /* 4 Cards per row */
                      @media(min-width: 1510px) { width: 1470px; }
                    `
                    : ''
    }
  `,
  productAdminScrollOverride: (hasEnvBanner: boolean) => css`
    height: calc(100vh - ${hasEnvBanner ? 185 : 130 }px);  /* Adjust for header height */
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,
};

export const LandingPage: React.FC = () => {
  const dispatch = useDispatch();

  const currentUser = useSelector((state: IAppState) => state.userState.firstName);
  const userRole = useSelector((state: IAppState) => state.userState.role);
  const environment = useSelector((state: IAppState) => state.resortToolsConfig.globalConfigs["environment"]);
  const hydrationEnabled = useSelector((state: IAppState) => state.resortToolsConfig.enableResortHydrationUIFeatures);
  const enableProductMapping = useSelector((state: IAppState) => state.resortToolsConfig.enableProductMapping);
  const enableMappingErrorBanner = useSelector((state: IAppState) => state.resortToolsConfig.enableMappingErrorBanner);
  const resetPassword = useSelector((state: IAppState) => state.userState.resetPassword);
  const selectedResort = useSelector((state: IAppState) => state.selectedResort);
  const hasError = useSelector((state: IAppState) => state.resortMappingHasError);
  const resources = useSelector((state: IAppState) => state.locale);
  const enableLanguage = useSelector((state: IAppState) => state.resortToolsConfig.enableLanguage);
  const languageCode = useSelector((state: IAppState) => state.userState.languagePreference);
  const resortLanguage = useSelector((state: IAppState) => state.resortToolsConfig.defaultLanguage || "en_us");
  const resortDateFormat = useSelector((state: IAppState) => state.resortToolsConfig.defaultDateFormat || "MM-dd-yyyy");
  const dateFormat = useSelector((state: IAppState) => state.userState.datePreference);
  const defaultDateFormat = useSelector((state: IAppState) => state.defaultDateFormat);

  const showResortSelectMenu = !useLocation().pathname.includes(PRODUCT_ADMINISTRATION);    
  const isProductAdmin = useLocation().pathname.includes(PRODUCT_ADMINISTRATION);
  const hasEnvBanner = environment?.toUpperCase() !== "PROD";

  const [isFinishedReset] = React.useState(false);

  useEffect(() => {
    dispatch(getResortMappingHasErrorAction(selectedResort.resortAbbreviation));
    dispatch(getResortDetailsAction())
    dispatch(getEntitlementTypesAction())

    // The product mapping loads belong to ResortSelectMenu, which waits for the selected resort's config
    // before reading its feature flags. Firing them here too only duplicates the request.

    if (hydrationEnabled){
      dispatch(getScriptVersionAction(selectedResort.resortAbbreviation))
    }

    dispatch(getUserSearchHistoryAction(selectedResort.resortId))
  },[dispatch, 
    enableProductMapping, 
    hydrationEnabled, 
    selectedResort.resortAbbreviation, 
    selectedResort.resortId, 
    userRole])

  useEffect(() => {
    if (resortLanguage && resortLanguage !== "" && resortLanguage !== resources["LOCALE_KEY"] && !languageCode) {
      dispatch(setLocale(resortLanguage))
    }
    else if (languageCode && languageCode !== resources["LOCALE_KEY"]) {
      dispatch(setLocale(languageCode))
    }
    if (resortDateFormat && resortDateFormat !== "" && resortDateFormat !== defaultDateFormat && !dateFormat) {
      dispatch(setDateFormat(resortDateFormat.replaceAll('-', '/')))
    }
    else if (dateFormat && dateFormat !== defaultDateFormat) {
      dispatch(setDateFormat(dateFormat.replaceAll('-', '/')));
    }
  },[resortLanguage, resortDateFormat, resources, languageCode, defaultDateFormat, dateFormat, dispatch])

  return  (
    <div css={styles.root(resetPassword)}>
      <div css={styles.headerWrapper}>
      {
        environment?.toUpperCase() !== 'PROD' && <span css={styles.envBanner}>{environment?.toUpperCase()}</span>
      }
      {
        !isProductAdmin && hasError && (userRole === 'admin' || userRole === 'sysadmin' || userRole === 'manager') && enableMappingErrorBanner && 
        <span css={styles.mappingBanner}>
          <img css={styles.mappingWarning} src={errorImgRed} alt=''/>
            {getResource(resources, 'MAPPING_ERROR_BANNER_WARNING')}
            <Link to={PRODUCT_MAPPING_ROUTE}>
              {getResource(resources, 'MAPPING_ERROR_BANNER_HERE')}
            </Link>
          <img css={styles.mappingWarning} src={errorImgRed} alt=''/>
        </span>
      }
      {
        !resetPassword && (
          <PageHeader
            resources={resources}
            currentUser={currentUser!}
            userRoleText={environment?.toUpperCase() !== 'PROD' ? `(${userRole})` : ''}
            enableLanguage={enableLanguage}
            showResortSelectMenu={showResortSelectMenu}
          />
      )}
      </div>
      <div css={styles.mainContentWrapper}>
        <div css={styles.mainContent}>
          {
            (resetPassword && !isFinishedReset)
              ? <ResetUserPassword />
              : (/* Child pages render based on nested routes */
                isProductAdmin ?                 
                <div css={styles.productAdminScrollOverride(hasEnvBanner)}>
                  <Outlet />
                </div>
                : <Outlet /> 
          )}
        </div>
      </div>
    </div>
  );
};